/**
 * @architecture-header 1
 * @layer module
 * @domain authentication
 * @responsibility Send organization-managed student credential requests through the injected FastAPI client.
 * @state-owner caller-owned request state
 * @allowed-imports @vispo/api-client; same-module credential schemas
 * @forbidden direct Supabase access; token persistence; authorization decisions
 * @public-api organization credential UI internals
 * @side-effects cookie-backed FastAPI authentication requests
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests frontend/packages/workspace-web/src/modules/auth/api/organization-credentials.test.ts
 * @security-boundary browser to cookie-backed FastAPI organization authentication
 * @data-classification passwords and one-time codes are sent only in request bodies
 * @trust-model FastAPI response and HttpOnly cookies are authoritative
 */

import type { VispoApiClient } from '@vispo/api-client';
import type {
  OrganizationActivationValues,
  OrganizationLoginValues,
} from '../schemas/organization-credential';

export interface OrganizationSession {
  status: 'authenticated';
  user_id: string;
  csrf_token: string;
  expires_at: string;
}

export async function loginWithOrganizationCredential(
  apiClient: VispoApiClient,
  values: OrganizationLoginValues,
): Promise<OrganizationSession> {
  return apiClient.request<OrganizationSession>('/api/v1/auth/org-login', {
    method: 'POST',
    body: { login_code: values.loginCode, password: values.password },
  });
}

export async function activateOrganizationCredential(
  apiClient: VispoApiClient,
  values: OrganizationActivationValues,
  legal: { termsVersion: string; privacyVersion: string },
): Promise<OrganizationSession> {
  return apiClient.request<OrganizationSession>('/api/v1/auth/org-activate', {
    method: 'POST',
    body: {
      login_code: values.loginCode,
      one_time_code: values.oneTimeCode,
      new_password: values.newPassword,
      terms_version: legal.termsVersion,
      privacy_version: legal.privacyVersion,
    },
  });
}

export async function organizationSession(apiClient: VispoApiClient): Promise<OrganizationSession> {
  return apiClient.request<OrganizationSession>('/api/v1/auth/org-session');
}

export async function logoutOrganizationSession(
  apiClient: VispoApiClient,
  csrfToken: string,
): Promise<void> {
  await apiClient.request('/api/v1/auth/org-logout', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken },
  });
}
